import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack
} from '@chakra-ui/react';

import ContentLayout from '@layouts/ContentLayout';
import Pagination from '@components/Pagination';
import KycCard from '@components/Kyc/KycCard';
import InputComponent from '@components/Input';
import FilterSelect from '@components/FiltersBox/FilterSelect';

import { updateKycsReqData } from '@store/actions/reqDataActions';
import { getAllKycsV2, getKycTags } from '@store/actions/kycActions';

import {
  KYC_PROVIDER,
  KYC_SUB_PROVIDER,
  KYC_V2_STATUS,
  VERIFICATIONS_ACTIVE_SEARCH
} from '@constants';
import { COUNTRY_ISO_CODES } from '@constants/v2/common';

import useDebounce from '@hooks/useDebounce';
import useDidMountEffect from '@hooks/useDidMount';

import {
  getDefaulProductForKycProvider,
  getDefaultKycProviderForProduct,
  getDefaultKycSubProviderForKycProvider,
  getProductsForActiveVerificationSearch,
  getProvidersForActiveVerificationSearch,
  getTextForProduct,
  getTextForProvider,
  toSentenceCase
} from '@utils';

const Kycs = () => {
  const { data: kycs, isLoading, totalPages } = useSelector(
    (state) => state.kyc.kycs
  );
  const { data: kycTags, isLoading: isTagsLoading } = useSelector(
    (state) => state.kyc.kycTags || {}
  );

  const dispatch = useDispatch();
  const searchReqData = useSelector((state) => state.reqData.kyc.kycs);

  const [userId, setUserId] = useState(searchReqData?.userId || '');
  const debouncedUserId = useDebounce(userId, 1000);

  useEffect(() => {
    dispatch(updateKycsReqData({ userId: debouncedUserId?.trim() }));
  }, [debouncedUserId]);

  useEffect(() => {
    setUserId(null);
  }, [searchReqData.activeTab]);

  useEffect(() => {
    if (kycs?.length) return;
    triggerGetKycs(searchReqData);
  }, []);

  useDidMountEffect(() => {
    triggerGetKycs(searchReqData);
  }, [
    searchReqData.product,
    searchReqData.provider,
    searchReqData.subProvider,
    searchReqData.status,
    searchReqData.pageNo,
    searchReqData.country,
    searchReqData.userId,
    searchReqData.tags,
    searchReqData.unselectedTags,
    searchReqData.subProviders
  ]);

  const triggerGetKycs = useCallback((searchReqData) => {
    dispatch(getAllKycsV2(searchReqData));
  }, [dispatch]);

  const mapTabIndexToActiveSearch = {
    0: VERIFICATIONS_ACTIVE_SEARCH.REMITTANCE
  };

  const getCountriesFromProvider = (provider) => {
    switch (provider) {
      case KYC_PROVIDER.ON_RAMP_MONEY:
        return [COUNTRY_ISO_CODES.AE, COUNTRY_ISO_CODES.GB];
      default:
        return [];
    }
  };

  const handleFilterSelectSubmit = useCallback(({ key, filters }) => {
    dispatch(updateKycsReqData({ [key]: filters, pageNo: 0 }));
  }, [dispatch]);

  const handleTabIndexChange = (idx) => {
    const activeSearch = mapTabIndexToActiveSearch[idx];
    const product = getProductsForActiveVerificationSearch(activeSearch)?.[0];
    const provider = getDefaultKycProviderForProduct(product);
    const subProvider = getDefaultKycSubProviderForKycProvider();

    activeSearch &&
    activeSearch !== searchReqData.activeSearch &&
    dispatch(
      updateKycsReqData({
        activeTab: idx,
        activeSearch,
        product,
        provider,
        subProvider,
        pageNo: 0,
        status: ''
      })
    );
  };

  const handlePageChange = useCallback(
    (pageNo) => {
      pageNo - 1 !== searchReqData.pageNo &&
      dispatch(
        updateKycsReqData({
          pageNo: pageNo - 1
        })
      );
    },
    [searchReqData.pageNo, dispatch]
  );

  useEffect(() => {
    dispatch(getKycTags());
  }, [dispatch]);

  const handlekycFilterChange = useCallback(
    (e) => {
      searchReqData.status !== e.target.value &&
      dispatch(updateKycsReqData({ status: e.target.value, pageNo: 0 }));
    },
    [dispatch, searchReqData.status]
  );

  const handleKycCountryChange = useCallback(
    (e) => {
      searchReqData.country !== e.target.value &&
      dispatch(updateKycsReqData({ country: e.target.value }));
    },
    [dispatch, searchReqData.country]
  );

  const handleProductChange = (product) => {
    const provider = getDefaultKycProviderForProduct(product);
    const subProvider = getDefaultKycSubProviderForKycProvider(provider);

    searchReqData.product !== product &&
    dispatch(
      updateKycsReqData({
        product,
        provider,
        subProvider,
        pageNo: 0,
        status: '',
        tags: [],
        unselectedTags: [],
        subProviders: []
      })
    );
  };

  const handleProviderChange = (provider) => {
    setUserId('');
    const product = getDefaulProductForKycProvider(provider);
    const subProvider = getDefaultKycSubProviderForKycProvider();

    searchReqData.provider !== provider &&
    dispatch(
      updateKycsReqData({
        product,
        provider,
        subProvider,
        pageNo: 0,
        status: '',
        tags: [],
        unselectedTags: [],
        subProviders: []
      })
    );
  };

  return (
    <ContentLayout>
      <Grid gridTemplateColumns={{ base: '1fr', lg: '7fr 0fr 3fr' }}>
        <VStack
          p={10}
          gap={4}
          overflowY={{ base: 'initial', lg: 'scroll' }}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          px={{ base: 4, sm: 10 }}
          alignItems={'stretch'}
          className="scroll"
        >
          <Flex w="100%" alignItems="baseline" justifyContent="space-between" pb={2}>
            <Heading color="white">Verification Dashboard</Heading>
          </Flex>

          <Tabs
            variant="enclosed"
            size="md"
            onChange={handleTabIndexChange}
            defaultIndex={searchReqData.activeTab}
          >
            <TabList color="white">
              {Object.keys(VERIFICATIONS_ACTIVE_SEARCH).map((searchKey) => (
                <Tab
                  key={searchKey}
                  _selected={{
                    color: 'brand.500',
                    border: '1px solid white',
                    borderBottom: '1px solid black'
                  }}
                >
                  {toSentenceCase(VERIFICATIONS_ACTIVE_SEARCH[searchKey])}
                </Tab>
              ))}
            </TabList>
          </Tabs>

          {getProductsForActiveVerificationSearch(searchReqData.activeSearch)?.length > 1 && (
            <RadioGroup
              alignSelf="start"
              color="white"
              onChange={handleProductChange}
              value={searchReqData.product}
            >
              <Stack spacing={5} direction="row" mb={5}>
                {getProductsForActiveVerificationSearch(
                  searchReqData.activeSearch
                ).map((product) => (
                  <Radio colorScheme="brand" key={product} value={product}>
                    {getTextForProduct(product)}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          )}

          {getProvidersForActiveVerificationSearch(searchReqData.activeSearch)?.length > 1 && (
            <RadioGroup
              alignSelf="start"
              color="white"
              onChange={handleProviderChange}
              value={searchReqData.provider}
            >
              <Stack spacing={5} direction="row" mb={5}>
                {getProvidersForActiveVerificationSearch(
                  searchReqData.activeSearch
                ).map((provider) => (
                  <Radio colorScheme="brand" key={provider} value={provider}>
                    {getTextForProvider(provider)}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          )}

          <VStack justifyContent="stretch" alignItems="start" gap={2}>
            <InputComponent
              placeholder="Search by User ID"
              handleChange={(e) => setUserId(e.target.value)}
              size="md"
              value={userId}
            />

            <HStack alignItems="start" gap={2}>
              <Select
                placeholder="Filter by status"
                onChange={handlekycFilterChange}
                bg="black"
                color="gray"
                value={searchReqData.status}
                size="sm"
              >
                {Object.keys(KYC_V2_STATUS).map((status) => (
                  <option value={status} key={status}>
                    {status}
                  </option>
                ))}
              </Select>
            </HStack>

            {!isTagsLoading && kycTags && (
              <HStack alignItems="start" gap={2}>
                <FilterSelect
                  filtersData={[
                    { key: 'tags', title: 'Include Tags', filters: kycTags }
                  ]}
                  handleFilterSubmit={handleFilterSelectSubmit}
                  defaultFiltersData={{
                    tags: searchReqData.tags || []
                  }}
                />
                <FilterSelect
                  filtersData={[
                    { key: 'unselectedTags', title: 'Exclude Tags', filters: kycTags }
                  ]}
                  handleFilterSubmit={handleFilterSelectSubmit}
                  defaultFiltersData={{
                    unselectedTags: searchReqData.unselectedTags || []
                  }}
                />
              </HStack>
            )}
            <HStack alignItems="start" gap={2}>
              <FilterSelect
                filtersData={[
                  {
                    key: 'subProviders',
                    title: 'Sub-Providers',
                    filters: Object.values(KYC_SUB_PROVIDER)
                  }
                ]}
                handleFilterSubmit={handleFilterSelectSubmit}
                defaultFiltersData={{
                  subProviders: searchReqData.subProviders || []
                }}
              />
            </HStack>

            {searchReqData.provider === KYC_PROVIDER.ON_RAMP_MONEY && (
              <HStack alignItems="start" gap={2}>
                <Select
                  placeholder="Country"
                  onChange={handleKycCountryChange}
                  bg="black"
                  color="gray"
                  value={searchReqData.country}
                  size="sm"
                >
                  {getCountriesFromProvider(searchReqData.provider).map(
                    (country) => (
                      <option value={country} key={country}>
                        {country}
                      </option>
                    )
                  )}
                </Select>
              </HStack>
            )}
          </VStack>

          {totalPages > 0 && (
            <Box alignSelf="start">
              <Box padding="20px" />
              <Pagination
                totalPages={totalPages}
                currentPage={searchReqData.pageNo + 1}
                onPageChange={handlePageChange}
              />
            </Box>
          )}

          {isLoading ? (
            <Flex justifyContent="center" alignItems="center" w="full" h="full">
              <Spinner thickness="4px" size="lg" color="colorPrimary" />
            </Flex>
          ) : kycs?.length ? (
            <VStack
              alignSelf="flex-start"
              w="100%"
              alignItems="flex-start"
              gap={4}
            >
              {kycs.map((item) => (
                <KycCard kyc={item} key={item.id} />
              ))}
            </VStack>
          ) : (
            <Box
              p={4}
              borderRadius="xl"
              bg="backGround"
              borderWidth="1px"
              borderColor="whiteAlpha.300"
            >
              <Text color="white" fontSize="md">
                No data found!
              </Text>
            </Box>
          )}
        </VStack>

        <Divider
          style={{ margin: '0' }}
          borderColor="whiteAlpha.300"
          orientation="vertical"
        />
      </Grid>
    </ContentLayout>
  );
};

export default Kycs;